import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery, ListItemButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Logostar from 'assets/images/star-circle.svg';
import clsx from 'clsx';
import { StarsLogo } from 'components/starsLogo';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import { useWorkspaceContext } from 'contexts/workspace-context';
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { usePreferences } from 'contexts/preferences-context';

import Autocomplete from './components/autocomplete';
import { MenuProfile } from './components/menu-profile';
import { NotificationsBell } from './components/notifications-bell';
import NotificationsPopup from './components/notifications-popup';
import SnackBar from './components/snanckBar';
import useLayoutHeader from './layout-header-hooks';
import { faEnvelopeOpenText, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useCheckUserPermissions } from 'hooks/useChekUserPermissions';
import { TITLE_COMPANY, NOTIFICATIONS, ROUTE_DISBURSEMENTS_AND_RECEIPTS } from 'utils/constants';

const useStyles = makeStyles((theme: any) => ({
  '@keyframes pulseAttention': {
    '0%': {
      filter: `drop-shadow(0 0 0px ${theme.accent.main})`,
    },
    '50%': {
      filter: `drop-shadow(0 0 8px ${theme.accent.main})`,
    },
    '100%': {
      filter: `drop-shadow(0 0 0px ${theme.accent.main})`,
    },
  },
  appBar: {
    backgroundColor: theme.palette.primary.main,
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mobileToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    minHeight: theme.spacing(7),
    [theme.breakpoints.up('md')]: {
      minHeight: theme.spacing(8),
      padding: theme.spacing(1, 3),
    },
  },
  logo: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      width: theme.spacing(8),
      height: theme.spacing(8),
    },
  },
  brandSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 0,
  },
  brandDivider: {
    height: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      height: theme.spacing(4),
    },
  },
  searchSection: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      padding: theme.spacing(0, 1),
      minWidth: 0,
      '& .MuiAutocomplete-root': {
        flex: 1,
        width: '100%',
        minWidth: 0,
      },
      '& .MuiInputBase-root': {
        flex: 1,
        width: '100%',
        minWidth: 0,
      },
      '& .MuiInputBase-input': {
        width: '100%',
        minWidth: 0,
        paddingLeft: `${theme.spacing(3)} !important`,
      },
      '& .MuiInputAdornment-positionStart': {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        height: theme.spacing(2),
        maxHeight: 'none',
        marginRight: 0,
      },
    },
  },
  mobileSearchSection: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  actionsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
  },
  desktopNav: {
    display: 'none',
  },
  desktopNavButtons: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  },
  navButton: {
    padding: theme.spacing(1, 2),
    minHeight: theme.spacing(6),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderColor: 'transparent',
    color: theme.palette.dualText.primary,
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      borderColor: 'transparent',
    },
    '&.active': {
      borderBottomColor: theme.accent.main,
      borderBottomWidth: 3,
      color: theme.accent.main,
    },
  },
  navIcon: {
    fontSize: theme.spacing(2.5),
  },
  drawer: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  drawerPaper: {
    backgroundColor: theme.palette.background.paper,
    width: 280,
  },
  drawerHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  drawerHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  drawerTitle: {
    fontSize: theme.spacing(2.5),
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  drawerContent: {
    padding: theme.spacing(1, 0),
  },
  drawerNavItem: {
    padding: theme.spacing(1.5, 2),
    color: theme.palette.text.primary,
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  drawerNavItemActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.dualText.primary,
  },
  drawerSection: {
    padding: theme.spacing(2, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    '& .MuiListItem-root': {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
  },
  drawerSectionTitle: {
    padding: theme.spacing(1, 2),
    fontSize: theme.spacing(1.5),
    fontWeight: 500,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
  },
  iconButton: {
    padding: theme.spacing(1),
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  menuButton: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  desktopActions: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginLeft: theme.spacing(2),
    },
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  badge: {
    backgroundColor: theme.palette.secondary.main,
  },
  avatarContainer: {
    position: 'relative',
  },
  stateBubble: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    position: 'absolute',
    borderRadius: '50%',
    right: -3,
    bottom: -3,
    border: `2px solid ${theme.palette.background.paper}`,
    background: theme.palette.primary.light,
  },
  stateBubbleActive: {
    background: theme.palette.success.main,
  },
  link: {
    textDecoration: 'none',
  },
  configMenu: {
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.dualText.primary,
    },
  },
  assistantButton: {
    color: theme.palette.common.white,
  },
  assistantButtonPulsing: {
    animation: '$pulseAttention 2s ease-in-out infinite',
    '&:hover, &:focus-visible': {
      animationPlayState: 'paused',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  utilityIcon: {
    color: theme.palette.common.white,
    fontSize: theme.spacing(2.5),
    opacity: 1,
  },
  listItemIcon: {
    minWidth: theme.spacing(4),
    color: 'inherit',
  },
  desktopNavBox: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  mobileSearchRow: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: theme.spacing(1, 2),
      backgroundColor: theme.palette.primary.main,
      borderBottom: `1px solid ${theme.palette.divider}`,
      gap: theme.spacing(1),
    },
  },
  mobileSearchRowActive: {
    display: 'flex',
  },
  mobileSearchInput: {
    flex: 1,
  },
  mobileSearchCloseButton: {
    padding: theme.spacing(1),
  },
  goldDivider: {
    backgroundColor: theme.accent.main,
  },
}));

type LayoutHeaderProps = {
  t: TFunction;
  agencySelected: string;
  selectAgency: (agencyId: string) => void;
  location: any;
  userIsTitleAdmin: boolean;
  currentLanguageCode: string;
  userLetters: string;
  anchorEl: HTMLElement | null;
  isMenuOpen: boolean;
  handleRoute: (path: string) => void;
  handleLanguage: (language: string) => void;
  handleProfileMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleNavigateProfile: () => void;
  handleNavigateTermsOfService: () => void;
  handleLogout: () => void;
  handleClose: () => void;
  handleTemplateClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  templateAnchorEl: HTMLElement | null;
  handleOpenAiUsage: (email: string | null) => void;
};

const LayoutHeader = ({
  t,
  agencySelected,
  selectAgency,
  location,
  userIsTitleAdmin,
  currentLanguageCode,
  userLetters,
  anchorEl,
  isMenuOpen,
  handleRoute,
  handleLanguage,
  handleProfileMenuOpen,
  handleNavigateProfile,
  handleNavigateTermsOfService,
  handleLogout,
  handleClose,
  handleTemplateClick,
  templateAnchorEl,
  handleOpenAiUsage,
}: LayoutHeaderProps) => {
  const classes = useStyles();
  const { theme: themeMode, setTheme } = usePreferences() as any;
  const isDarkMode = themeMode === 'dark';
  const toggleTheme = () => setTheme(isDarkMode ? 'light' : 'dark');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [agencySelectorAnchorEl, setAgencySelectorAnchorEl] = useState<HTMLElement | null>(null);

  const {
    options,
    loading,
    updateSearchText,
    isConnectedSocket,
    notifications,
    newNotification,
    handleClickNotifications,
    anchorNotifications,
    openNotifications,
    handleCloseNotifications,
    currentUser,
    permission,
    enableDesktopNotifications,
    clearNotifications,
    openOrderFromNotification,
  } = useLayoutHeader();
  const { toggleAgentSlideOpen, agentSlideOpen } = useLLMProxyServiceContext();
  const { selectedOrderIdTab } = useWorkspaceContext();

  const { hasPermission } = useCheckUserPermissions(currentUser);

  const isInOrderView = selectedOrderIdTab && location.pathname.includes('/orders/details/');

  const hasChargesPermission = hasPermission('createChargeSetting');
  const hasTemplatesPermission = hasPermission('createAutoTemplate');
  const hasEmailTemplatesPermission = hasPermission('createEmail');
  const hasRolePermission = hasPermission('createRole');
  // Same rule as the Imports route: text extraction and inference need both permissions.
  const hasImportsPermission = hasPermission('getAnalyzeDocument') && hasPermission('getAiUsage');
  const showConfigurationMenu =
    hasChargesPermission || hasTemplatesPermission || hasRolePermission || hasEmailTemplatesPermission;
  const loginEmail = currentUser?.emails.find((email) => email.kind === 'Login')?.address;

  const titleCompanyParent = currentUser?.accesses.find(({ kind }) => kind === TITLE_COMPANY);
  const titleSubCompanies = currentUser?.accesses.filter(({ kind }) => kind !== TITLE_COMPANY).sort((a, b) => a.name.localeCompare(b.name));

  const getSelectedAgencyName = () => {
    if (!agencySelected) {
      return t('menu:all-agencies');
    }
    if (titleCompanyParent?._id === agencySelected) {
      return titleCompanyParent.name;
    }
    return titleSubCompanies?.find((a) => a._id === agencySelected)?.name || agencySelected;
  };

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setAgencySelectorAnchorEl(null);
  };

  const handleDrawerNavClick = (path: string) => {
    handleRoute(path);
    handleDrawerClose();
  };

  const handleAgencySelectorOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAgencySelectorAnchorEl(event.currentTarget);
  };

  const handleAgencySelectorClose = () => {
    setAgencySelectorAnchorEl(null);
  };

  const handleAgencySelect = (agencyId: string) => {
    selectAgency(agencyId);
    handleAgencySelectorClose();
  };

  const isPathActive = (path: string) => {
    if (path.includes('/')) {
      return location.pathname.includes(path);
    }
    return location.pathname === path;
  };

  return (
    <>
      <AppBar position="fixed" role="banner" className={classes.appBar}>
        <Toolbar className={classes.mobileToolbar}>
          {/* Brand section - logo + divider */}
          <div className={classes.brandSection}>
            <img alt="Championship Title logo" src={Logostar} className={classes.logo} />
            <Divider
              orientation="vertical"
              className={clsx(classes.brandDivider, classes.goldDivider)}
            />
          </div>

          {/* Desktop search - shows on md and up */}
          <div className={classes.searchSection}>
            {!isMobile && (
            <Autocomplete t={t} options={options} loading={loading} updateSearchText={updateSearchText} />
            )}
          </div>

          {/* Desktop navigation buttons - shows on md and up */}
          <div className={classes.desktopNavButtons}>
            <Box className={classes.desktopNavBox}>
              <Link to="/orders" className={classes.link}>
                <Button
                  className={clsx(classes.navButton, {
                    active: location.pathname === '/orders',
                  })}
                  onClick={() => handleRoute('/orders')}
                  aria-current={location.pathname === '/orders' ? 'page' : undefined}
                >
                  <FontAwesomeIcon icon="archive" className={classes.navIcon} />
                  <Typography variant="body2">{t('common:orders')}</Typography>
                </Button>
              </Link>

              <Link to="/orders/details" className={classes.link}>
                <Button
                  className={clsx(classes.navButton, {
                    active: location.pathname.includes('/orders/details'),
                  })}
                  onClick={() => handleRoute('/orders/details')}
                  aria-current={location.pathname.includes('/orders/details') ? 'page' : undefined}
                >
                  <FontAwesomeIcon icon="folder-open" className={classes.navIcon} />
                  <Typography variant="body2">{t('common:workspace')}</Typography>
                </Button>
              </Link>

              {userIsTitleAdmin && hasPermission('createParty') && (
              <Link to="/contacts" className={classes.link}>
                <Button
                  className={clsx(classes.navButton, {
                    active: location.pathname.includes('/contacts'),
                  })}
                  onClick={() => handleRoute('/contacts')}
                  aria-current={location.pathname.includes('/contacts') ? 'page' : undefined}
                >
                  <FontAwesomeIcon icon="address-book" className={classes.navIcon} />
                  <Typography variant="body2">{t('common:contacts')}</Typography>
                </Button>
              </Link>
              )}

              {hasImportsPermission && (
              <Link to="/imports" className={classes.link}>
                <Button
                  className={clsx(classes.navButton, {
                    active: location.pathname.includes('/imports'),
                  })}
                  onClick={() => handleRoute('/imports')}
                  aria-current={location.pathname.includes('/imports') ? 'page' : undefined}
                >
                  <FontAwesomeIcon icon="file-import" className={classes.navIcon} />
                  <Typography variant="body2">{t('imports:title')}</Typography>
                </Button>
              </Link>
              )}

              {showConfigurationMenu && (
              <>
                <Button
                  className={clsx(classes.navButton, {
                    active: isPathActive('/document-templates') ||
                      isPathActive('/email-templates') ||
                      isPathActive('/charge-settings'),
                  })}
                  onClick={handleTemplateClick}
                  aria-haspopup="true"
                >
                  <FontAwesomeIcon icon="cog" className={classes.navIcon} />
                  <Typography variant="body2">{t('common:configuration')}</Typography>
                  <FontAwesomeIcon icon="caret-down" className={classes.navIcon} size="sm" />
                </Button>

                <Menu
                  keepMounted
                  anchorEl={templateAnchorEl}
                  open={Boolean(templateAnchorEl)}
                  onClose={() => handleTemplateClick()}
                  className={classes.configMenu}
                >
                  {userIsTitleAdmin && (
                  <MenuItem>
                    <Link to="/ai-config" className={classes.link}>
                      <Button
                        className={classes.navButton}
                        onClick={() => {
                          handleRoute('/ai-config');
                          handleTemplateClick();
                        }}
                      >
                        <FontAwesomeIcon icon="cog" className={classes.navIcon} />
                        <Typography variant="body2">{t('aiConfig:title')}</Typography>
                      </Button>
                    </Link>
                  </MenuItem>
                  )}

                  {hasChargesPermission && (
                  <MenuItem>
                    <Link to="/charge-settings" className={classes.link}>
                      <Button
                        className={classes.navButton}
                        onClick={() => {
                          handleRoute('/charge-settings');
                          handleTemplateClick();
                        }}
                      >
                        <FontAwesomeIcon icon="dollar-sign" className={classes.navIcon} />
                        <Typography variant="body2">{t('menu:chargeSettings')}</Typography>
                      </Button>
                    </Link>
                  </MenuItem>
                  )}

                  {hasTemplatesPermission && (
                  <MenuItem>
                    <Link to="/document-templates" className={classes.link}>
                      <Button
                        className={classes.navButton}
                        onClick={() => {
                          handleRoute('/document-templates');
                          handleTemplateClick();
                        }}
                      >
                        <FontAwesomeIcon icon="file" className={classes.navIcon} />
                        <Typography variant="body2">{t('documents:document-templates')}</Typography>
                      </Button>
                    </Link>
                  </MenuItem>
                  )}

                  {hasEmailTemplatesPermission && (
                  <MenuItem>
                    <Link to="/email-templates" className={classes.link}>
                      <Button
                        className={classes.navButton}
                        onClick={() => {
                          handleRoute('/email-templates');
                          handleTemplateClick();
                        }}
                      >
                        <FontAwesomeIcon icon={faEnvelopeOpenText} className={classes.navIcon} />
                        <Typography variant="body2">{t('emailTemplates:email-templates')}</Typography>
                      </Button>
                    </Link>
                  </MenuItem>
                  )}

                  {hasRolePermission && (
                  <MenuItem>
                    <Link to="/roles" className={classes.link}>
                      <Button
                        className={classes.navButton}
                        onClick={() => {
                          handleRoute('/roles');
                          handleTemplateClick();
                        }}
                      >
                        <FontAwesomeIcon icon={faUserShield} className={classes.navIcon} />
                        <Typography variant="body2">{t('roles:roles')}</Typography>
                      </Button>
                    </Link>
                  </MenuItem>
                  )}
                </Menu>
              </>
              )}

              <Button
                className={classes.navButton}
                onClick={() => handleLanguage(currentLanguageCode === 'es' ? 'en' : 'es')}
                aria-label={t(currentLanguageCode === 'en' ? 'common:change-to-spanish' : 'common:change-to-english')}
              >
                <FontAwesomeIcon icon="language" size="sm" className={classes.navIcon} />
                <Typography variant="body2">
                  {t(currentLanguageCode === 'en' ? 'common:target-spanish' : 'common:target-english')}
                </Typography>
              </Button>
            </Box>
          </div>

          {/* Actions section - right side */}
          <div className={classes.actionsSection}>
            {/* Mobile search button */}
            {isMobile && (
            <IconButton
              className={classes.iconButton}
              aria-label="Find order"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen(!searchOpen)}
              size="large"
            >
              <FontAwesomeIcon icon="search" className={classes.utilityIcon} />
            </IconButton>
            )}

            {/* Notifications */}
            <NotificationsBell
              t={t}
              unreadCount={notifications.filter((n) => !n.viewed).length}
              onClick={handleClickNotifications}
              classes={{ iconButton: classes.iconButton, utilityIcon: classes.utilityIcon, badge: classes.badge }}
            />

            {/* Theme toggle */}
            <IconButton
              className={classes.iconButton}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
              size="large"
            >
              <FontAwesomeIcon icon={isDarkMode ? 'sun' : 'moon'} className={classes.utilityIcon} />
            </IconButton>

            {/* Assistant (stars logo) */}
            <IconButton
              className={clsx(classes.iconButton, classes.assistantButton, {
                [classes.assistantButtonPulsing]: !agentSlideOpen,
              })}
              aria-label="Open assistant"
              title="Assistant"
              onClick={toggleAgentSlideOpen}
              size="large"
            >
              <StarsLogo />
            </IconButton>

            {/* Mobile menu button */}
            <IconButton
              className={classes.menuButton}
              aria-label="Open navigation"
              aria-haspopup="true"
              onClick={handleDrawerOpen}
              size="large"
            >
              <FontAwesomeIcon icon="bars" className={classes.utilityIcon} />
            </IconButton>

            {/* Desktop language + profile menu */}
            <div className={classes.desktopActions}>
              <IconButton
                className={classes.iconButton}
                aria-label="Open profile"
                onClick={handleProfileMenuOpen}
                size="large"
              >
                <Box className={classes.avatarContainer}>
                  <Avatar>{userLetters}</Avatar>
                  <Box
                    className={clsx(classes.stateBubble, {
                      [classes.stateBubbleActive]: isConnectedSocket,
                    })}
                  />
                </Box>
              </IconButton>
            </div>
          </div>
        </Toolbar>

        {isMobile && searchOpen && (
        <div className={clsx(classes.mobileSearchRow, classes.mobileSearchRowActive)}>
          <div className={classes.mobileSearchInput}>
            <Autocomplete
              t={t}
              options={options}
              loading={loading}
              updateSearchText={updateSearchText}
              autoFocus
            />
          </div>
          <IconButton
            className={classes.mobileSearchCloseButton}
            aria-label="Close search"
            onClick={() => setSearchOpen(false)}
            size="large"
          >
            <FontAwesomeIcon icon="times" className={classes.utilityIcon} />
          </IconButton>
        </div>
        )}

      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        className={classes.drawer}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Drawer header */}
        <div className={classes.drawerHeader}>
          <Box className={classes.drawerHeaderContent}>
            <Box className={classes.avatarContainer}>
              <Avatar>{userLetters}</Avatar>
              <Box
                className={clsx(classes.stateBubble, {
                  [classes.stateBubbleActive]: isConnectedSocket,
                })}
              />
            </Box>
            <Typography className={classes.drawerTitle}>{t('common:menu')}</Typography>
          </Box>
          <IconButton
            className={classes.iconButton}
            aria-label="Close navigation"
            onClick={handleDrawerClose}
            size="large"
          >
            <FontAwesomeIcon icon="times" size="lg" />
          </IconButton>
        </div>

        {/* Drawer content */}
        <div className={classes.drawerContent}>
          {/* Main navigation */}
          <List>
            {/* Agency Selector */}
            <ListItemButton
              className={classes.drawerNavItem}
              onClick={handleAgencySelectorOpen}
              aria-haspopup="true"
              aria-expanded={Boolean(agencySelectorAnchorEl)}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon="home" />
              </ListItemIcon>
              <ListItemText primary={getSelectedAgencyName()} />
            </ListItemButton>

            <Link to="/orders" className={classes.link}>
              <ListItemButton
                className={clsx(classes.drawerNavItem, {
                  [classes.drawerNavItemActive]: location.pathname === '/orders',
                })}
                onClick={() => handleDrawerNavClick('/orders')}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="archive" />
                </ListItemIcon>
                <ListItemText primary={t('common:orders')} />
              </ListItemButton>
            </Link>

            <Link to="/orders/details" className={classes.link}>
              <ListItemButton
                className={clsx(classes.drawerNavItem, {
                  [classes.drawerNavItemActive]: location.pathname.includes('/orders/details'),
                })}
                onClick={() => handleDrawerNavClick('/orders/details')}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="folder-open" />
                </ListItemIcon>
                <ListItemText primary={t('common:workspace')} />
              </ListItemButton>
            </Link>

            {userIsTitleAdmin && hasPermission('createParty') && (
            <Link to="/contacts" className={classes.link}>
              <ListItemButton
                className={clsx(classes.drawerNavItem, {
                  [classes.drawerNavItemActive]: location.pathname.includes('/contacts'),
                })}
                onClick={() => handleDrawerNavClick('/contacts')}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="address-book" />
                </ListItemIcon>
                <ListItemText primary={t('common:contacts')} />
              </ListItemButton>
            </Link>
            )}

            {hasImportsPermission && (
              <Link to="/imports" className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/imports'),
                  })}
                  onClick={() => handleDrawerNavClick('/imports')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="file-import" />
                  </ListItemIcon>
                  <ListItemText primary={t('imports:title')} />
                </ListItemButton>
              </Link>
            )}
          </List>

          {/* Order Menu section (mobile only, when viewing an order) */}
          {isInOrderView && (
          <div className={classes.drawerSection}>
            <div className={classes.drawerSectionTitle}>{t('orders:order-menu')}</div>
            <List>
              {/* General section */}
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/overview` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/overview'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/overview` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="briefcase" />
                  </ListItemIcon>
                  <ListItemText primary={t('orders:overview')} />
                </ListItemButton>
              </Link>

              {hasPermission('createOrderLedgerEntry') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/cash-to-close` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/cash-to-close'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/cash-to-close` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="money-bill" />
                  </ListItemIcon>
                  <ListItemText primary={t('disclosure:cash-to-close')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderLedgerEntry') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/charges` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/charges'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/charges` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="receipt" />
                  </ListItemIcon>
                  <ListItemText primary={t('common:charges')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('updateOrderConfiguration') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/dates-and-config` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/dates-and-config'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/dates-and-config` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="cog" />
                  </ListItemIcon>
                  <ListItemText primary={t('orders:date-and-config')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderFile') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/documents` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/documents'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/documents` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="file" />
                  </ListItemIcon>
                  <ListItemText primary={t('orders:documents')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderLedgerEntry') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/earnest-and-commissions` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/earnest-and-commissions'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/earnest-and-commissions` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="briefcase" />
                  </ListItemIcon>
                  <ListItemText primary={t('earnestAndCommissions:earnest-and-commissions')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderLoan') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/loans` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/loans'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/loans` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="file-contract" />
                  </ListItemIcon>
                  <ListItemText primary={t('loan:loans')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderMortgage') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payoffs` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/payoffs'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payoffs` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="home" />
                  </ListItemIcon>
                  <ListItemText primary={t('payoffs:payoffs')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderNote') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/notes` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/notes'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/notes` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="sticky-note" />
                  </ListItemIcon>
                  <ListItemText primary={t('notes:notes')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('sendNotification') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/${NOTIFICATIONS}` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes(`/${NOTIFICATIONS}`),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/${NOTIFICATIONS}` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="bell" />
                  </ListItemIcon>
                  <ListItemText primary={t('messages:notifications')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderAssociation') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/parties` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/parties'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/parties` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="users" />
                  </ListItemIcon>
                  <ListItemText primary={t('parties:parties')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderLedgerEntry') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/policy-info-and-rates` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/policy-info-and-rates'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/policy-info-and-rates` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="file-alt" />
                  </ListItemIcon>
                  <ListItemText primary={t('policyInfoAndRates:policy-info-and-rates')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('updateOrderProjected') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/projected-payments` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/projected-payments'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/projected-payments` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="chart-line" />
                  </ListItemIcon>
                  <ListItemText primary={t('disclosure:projected-payments')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderProperty') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/properties` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/properties'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/properties` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="building" />
                  </ListItemIcon>
                  <ListItemText primary={t('orders:properties')} />
                </ListItemButton>
              </Link>
              )}

              {hasPermission('createOrderProration') && (
              <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/taxes-and-prorations` : '#'} className={classes.link}>
                <ListItemButton
                  className={clsx(classes.drawerNavItem, {
                    [classes.drawerNavItemActive]: location.pathname.includes('/taxes-and-prorations'),
                  })}
                  onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/taxes-and-prorations` : '#')}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <FontAwesomeIcon icon="percentage" />
                  </ListItemIcon>
                  <ListItemText primary={t('taxesAndProrations:taxes-and-prorations')} />
                </ListItemButton>
              </Link>
              )}

              {/* Payments section */}
              {(hasPermission('createOrderLedgerEntry') || hasPermission('createTransferRequest')) && (
              <>
                <Box style={{ borderTop: `1px solid ${theme.palette.divider}`, marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }} />
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/accounting` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/payments/accounting'),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/accounting` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="money-bill-wave-alt" />
                    </ListItemIcon>
                    <ListItemText primary={t('accounting:accounting')} />
                  </ListItemButton>
                </Link>

                {hasPermission('createOrderLedgerEntry') && (
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/${ROUTE_DISBURSEMENTS_AND_RECEIPTS}` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes(ROUTE_DISBURSEMENTS_AND_RECEIPTS),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/${ROUTE_DISBURSEMENTS_AND_RECEIPTS}` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="receipt" />
                    </ListItemIcon>
                    <ListItemText primary={t('payments:disbursements-and-receipts')} />
                  </ListItemButton>
                </Link>
                )}

                {hasPermission('createOrderLedgerEntry') && (
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/revenue` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/revenue'),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/revenue` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="chart-pie" />
                    </ListItemIcon>
                    <ListItemText primary={t('revenue:title')} />
                  </ListItemButton>
                </Link>
                )}

                {hasPermission('createOrderLedgerEntry') && (
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/settlement` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/payments/settlement'),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/settlement` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="handshake" />
                    </ListItemIcon>
                    <ListItemText primary={t('payments:settlement')} />
                  </ListItemButton>
                </Link>
                )}

                {hasPermission('createTransferRequest') && (
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/wire-transfers` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/payments/wire-transfers'),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/payments/wire-transfers` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="exchange-alt" />
                    </ListItemIcon>
                    <ListItemText primary={t('payments:wire-tranfers')} />
                  </ListItemButton>
                </Link>
                )}
              </>
              )}

              {/* Closing section */}
              {hasPermission('updateOrderTokens') && (
              <>
                <Box style={{ borderTop: `1px solid ${theme.palette.divider}`, marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }} />
                <Link to={selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/closing/schedule-closing` : '#'} className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/closing/schedule-closing'),
                    })}
                    onClick={() => handleDrawerNavClick(selectedOrderIdTab ? `/orders/details/${selectedOrderIdTab.id}/closing/schedule-closing` : '#')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="file-alt" />
                    </ListItemIcon>
                    <ListItemText primary={t('closing:schedule-closing')} />
                  </ListItemButton>
                </Link>
              </>
              )}
            </List>
          </div>
          )}

          {/* Configuration section */}
          {showConfigurationMenu && (
          <>
            <div className={classes.drawerSection}>
              <div className={classes.drawerSectionTitle}>{t('common:configuration')}</div>
              <List>
                {userIsTitleAdmin && (
                <Link to="/ai-config" className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/ai-config'),
                    })}
                    onClick={() => handleDrawerNavClick('/ai-config')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="cog" />
                    </ListItemIcon>
                    <ListItemText primary={t('aiConfig:title')} />
                  </ListItemButton>
                </Link>
                )}

                {hasChargesPermission && (
                <Link to="/charge-settings" className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/charge-settings'),
                    })}
                    onClick={() => handleDrawerNavClick('/charge-settings')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="dollar-sign" />
                    </ListItemIcon>
                    <ListItemText primary={t('menu:chargeSettings')} />
                  </ListItemButton>
                </Link>
                )}

                {hasTemplatesPermission && (
                <Link to="/document-templates" className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/document-templates'),
                    })}
                    onClick={() => handleDrawerNavClick('/document-templates')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon="file" />
                    </ListItemIcon>
                    <ListItemText primary={t('documents:document-templates')} />
                  </ListItemButton>
                </Link>
                )}

                {hasEmailTemplatesPermission && (
                <Link to="/email-templates" className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/email-templates'),
                    })}
                    onClick={() => handleDrawerNavClick('/email-templates')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon={faEnvelopeOpenText} />
                    </ListItemIcon>
                    <ListItemText primary={t('emailTemplates:email-templates')} />
                  </ListItemButton>
                </Link>
                )}

                {hasRolePermission && (
                <Link to="/roles" className={classes.link}>
                  <ListItemButton
                    className={clsx(classes.drawerNavItem, {
                      [classes.drawerNavItemActive]: location.pathname.includes('/roles'),
                    })}
                    onClick={() => handleDrawerNavClick('/roles')}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <FontAwesomeIcon icon={faUserShield} />
                    </ListItemIcon>
                    <ListItemText primary={t('roles:roles')} />
                  </ListItemButton>
                </Link>
                )}
              </List>
            </div>
          </>
          )}

          {/* Mobile actions section */}
          <div className={classes.drawerSection}>
            <List>
              <ListItemButton
                className={classes.drawerNavItem}
                aria-label="Switch language"
                onClick={() => {
                  handleLanguage(currentLanguageCode === 'es' ? 'en' : 'es');
                  handleDrawerClose();
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="language" />
                </ListItemIcon>
                <ListItemText primary={t(currentLanguageCode === 'en' ? 'common:change-to-spanish' : 'common:change-to-english')} />
              </ListItemButton>

              <ListItemButton
                className={classes.drawerNavItem}
                onClick={() => {
                  handleNavigateProfile();
                  handleDrawerClose();
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="user" />
                </ListItemIcon>
                <ListItemText primary={t('users:profile')} />
              </ListItemButton>

              <ListItemButton
                className={classes.drawerNavItem}
                disabled={!loginEmail}
                onClick={() => {
                  if (loginEmail) {
                    handleOpenAiUsage(loginEmail);
                    handleDrawerClose();
                  }
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="chart-area" />
                </ListItemIcon>
                <ListItemText primary={t('aiUsage:button')} />
              </ListItemButton>

              <ListItemButton
                className={classes.drawerNavItem}
                onClick={() => {
                  handleLogout();
                  handleDrawerClose();
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="sign-out-alt" />
                </ListItemIcon>
                <ListItemText primary={t('users:log-out')} />
              </ListItemButton>

              <ListItemButton
                className={classes.drawerNavItem}
                onClick={() => {
                  handleNavigateTermsOfService();
                  handleDrawerClose();
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon="file" />
                </ListItemIcon>
                <ListItemText primary={t('common:terms-of-service')} />
              </ListItemButton>
            </List>
          </div>
        </div>
      </Drawer>

      {/* Agency Selector Menu */}
      <Menu
        anchorEl={agencySelectorAnchorEl}
        open={Boolean(agencySelectorAnchorEl)}
        onClose={handleAgencySelectorClose}
      >
        <MenuItem
          selected={agencySelected === ''}
          onClick={() => handleAgencySelect('')}
        >
          {t('menu:all-agencies')}
        </MenuItem>

        {titleCompanyParent && (
        <MenuItem
          selected={agencySelected === titleCompanyParent._id}
          onClick={() => handleAgencySelect(titleCompanyParent._id)}
        >
          {titleCompanyParent.name}
        </MenuItem>
        )}

        {titleSubCompanies && titleSubCompanies.map((agency) => (
          <MenuItem
            key={agency._id}
            selected={agencySelected === agency._id}
            onClick={() => handleAgencySelect(agency._id)}
          >
            {agency.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Profile menu popover */}
      <MenuProfile
        anchorEl={anchorEl}
        open={isMenuOpen}
        handleClose={handleClose}
        handleNavigateProfile={handleNavigateProfile}
        handleNavigateTermsOfService={handleNavigateTermsOfService}
        handleLogout={handleLogout}
        selectAgency={selectAgency}
        agencySelected={agencySelected}
        t={t}
        handleOpenAiUsage={handleOpenAiUsage}
      />

      {/* Notifications popup */}
      <NotificationsPopup
        anchorNotifications={anchorNotifications}
        openNotifications={openNotifications}
        handleCloseNotifications={handleCloseNotifications}
        notifications={notifications}
        t={t}
        permission={permission}
        onEnableDesktop={enableDesktopNotifications}
        onClear={clearNotifications}
        onOpenOrder={openOrderFromNotification}
      />

      {/* New notification snackbar */}
      {newNotification && <SnackBar t={t} notification={newNotification} />}
    </>
  );
};

export default LayoutHeader;
